import { Button, Stack, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { selectLanguage, selectTranslations } from 'state/reducers/translation.reducers';
import { FormHeader1 } from 'theme/elements/Headers';
import { Page } from 'theme/elements/Page';
import { FormButton } from 'theme/elements/Buttons';
import { CenteredNarrowContent } from 'theme/elements/Content';
import { FormCard } from 'theme/elements/Forms';
import {
	getMessage,
	getReportDates,
	getReportsCatalog,
	IReportCatalogItem,
	getSelectedReport,
	getSelectedReportKey,
	isLoading,
	ReportFormat,
	ReportLocale,
	setMessage,
	setReportDates,
	setSelectedReportKey,
	resetReportsForm,
} from 'state/reducers/reports.reducers';
import { getBeneficiaries, getLoadingBeneficiaries } from 'state/reducers/beneficiary.reducers';
import { fieldsGroupType, fieldType } from 'components/FormCreator/types';
import FormCreator from 'components/FormCreator';
import {
	formatDate,
	ReportInputFields,
	reportsCatalogTranslationKeys,
	WARNING_EXCLAMATION_ICON,
} from 'constants/reports.constants';
import ImagePopup from 'components/ImagePopup';
import { defineds } from 'constants/ui.constants';
import { downloadReport, fetchReportsCatalog } from 'services/reports.services';
import type { ReportRenderFilters } from 'services/reports.services';
import { loadBeneficiaries } from 'services/beneficiary.services';
import { addDays, format as formatDateFns, isValid as isValidDate, startOfDay } from 'date-fns';
import { downloadBlobResponse } from 'utils';

const getBalancesMaxDate = () => startOfDay(addDays(new Date(), -1));
const isBalancesSingleDateReport = (report: IReportCatalogItem | null) =>
	Boolean(report?.reportKey === 'balances_report' && report.filterSpec.type === 'date');

/** Safe segment for download filenames (keeps letters including Hebrew; strips path/hostile chars). */
const sanitizeReportFileSegment = (raw: string): string => {
	const s = String(raw ?? '')
		.trim()
		.replace(/[<>:"/\\|?*\u0000-\u001f]+/g, '-')
		.replace(/\s+/g, '-')
		.replace(/-+/g, '-')
		.replace(/^-|-$/g, '');
	return s || 'beneficiary';
};

const buildReportFileName = (
	report: IReportCatalogItem,
	filterType: string,
	filters: Record<string, any>,
	locale: string,
	fileFormat: string,
	t: (key: string, fallback?: string) => string,
	beneficiaryDisplayName?: string
): string => {
	const translationKey = reportsCatalogTranslationKeys[report.reportKey];
	const displayName = locale === 'he' && translationKey
		? t(translationKey, report.displayName)
		: report.displayName;
	const reportName = (displayName || 'Report').replace(/\s+/g, '-');
	const base = `OPEN_${reportName}`;
	const suffix = `${locale}.${fileFormat}`;

	if (
		filterType === 'beneficiaryDateRange' &&
		filters.startDate &&
		filters.endDate
	) {
		const beneficiaryPart = sanitizeReportFileSegment(
			beneficiaryDisplayName ||
				(filters.beneficiaryId != null ? `id-${filters.beneficiaryId}` : '')
		);
		return `${base}_${beneficiaryPart}_${filters.startDate}_${filters.endDate}_${suffix}`;
	}

	if ((filterType === 'scope' || filterType === 'dateRange') && filters.startDate && filters.endDate) {
		return `${base}_${filters.startDate}_${filters.endDate}_${suffix}`;
	}

	if (filterType === 'date' && filters.date) {
		return `${base}_${filters.date}_${suffix}`;
	}

	return `${base}_${suffix}`;
};

export default function ReportsApp() {
	const dispatch = useAppDispatch();
	const lang = useAppSelector(selectLanguage);
	const t = useAppSelector(selectTranslations);
	const loading = useAppSelector(isLoading);
	const userMessage = useAppSelector(getMessage);
	const reportCatalog = useAppSelector(getReportsCatalog);
	const selectedReportKey = useAppSelector(getSelectedReportKey);
	const selectedReport = useAppSelector(getSelectedReport);
	const reportDates = useAppSelector(getReportDates);
	const beneficiaries = useAppSelector(getBeneficiaries);
	const loadBeneficiariesState = useAppSelector(getLoadingBeneficiaries);
	const [selectedFormat, setSelectedFormat] = useState<ReportFormat>('pdf');
	const [reportLocale, setReportLocale] = useState<ReportLocale>('he');
	const [reportScope, setReportScope] = useState<'all' | 'byCreationDate'>('all');
	const [selectedBeneficiaryId, setSelectedBeneficiaryId] = useState<string>('');
	const [beneficiariesRequested, setBeneficiariesRequested] = useState(false);

	const prefixFields = 'page.reports.';
	const title = t(`${prefixFields}title`);
	const subtitle = t(`${prefixFields}subtitle.type_and_date`);
	const reportTypeLabel = t(`${prefixFields}report_type.label`);
	const reportTypePlaceholder = t(`${prefixFields}report_type.placeholder`);
	const dateLabel = t(`${prefixFields}date.label`);
	const dateRangeLabel = t(`${prefixFields}date_range.placeholder`);

	const filters = useMemo<ReportRenderFilters>(() => {
		if (!selectedReport) {
			return {} as Record<string, never>;
		}

		if (selectedReport.filterSpec.type === 'none') {
			return {} as Record<string, never>;
		}

		if (selectedReport.filterSpec.type === 'date') {
			return {
				date: formatDateFns(new Date(reportDates.startDate), 'yyyy-MM-dd'),
			} as { date: string };
		}

		if (selectedReport.filterSpec.type === 'scope') {
			if (reportScope === 'all') {
				return { scope: 'all' } as { scope: 'all' };
			}
			return {
				scope: 'byCreationDate',
				startDate: formatDateFns(new Date(reportDates.startDate), 'yyyy-MM-dd'),
				endDate: formatDateFns(new Date(reportDates.endDate), 'yyyy-MM-dd'),
			} as { scope: 'byCreationDate'; startDate: string; endDate: string };
		}

		if (selectedReport.filterSpec.type === 'beneficiaryDateRange') {
			return {
				beneficiaryId: Number(selectedBeneficiaryId),
				startDate: formatDateFns(new Date(reportDates.startDate), 'yyyy-MM-dd'),
				endDate: formatDateFns(new Date(reportDates.endDate), 'yyyy-MM-dd'),
			} as { beneficiaryId: number; startDate: string; endDate: string };
		}

		return {
			startDate: formatDateFns(new Date(reportDates.startDate), 'yyyy-MM-dd'),
			endDate: formatDateFns(new Date(reportDates.endDate), 'yyyy-MM-dd'),
		} as { startDate: string; endDate: string };
	}, [selectedBeneficiaryId, selectedReport, reportDates, reportScope]);

	const selectedBeneficiaryDisplayName = useMemo(() => {
		if (!selectedBeneficiaryId) {
			return '';
		}
		const beneficiary = beneficiaries.find((b: any) => String(b.id) === String(selectedBeneficiaryId));
		if (!beneficiary) {
			return '';
		}
		return (
			beneficiary.nickname ||
			beneficiary.companyName ||
			`${beneficiary.firstName || ''} ${beneficiary.lastName || ''}`.trim() ||
			String(beneficiary.id)
		);
	}, [beneficiaries, selectedBeneficiaryId]);

	const dateRangeValue = useMemo(() => {
		if (!selectedReport || selectedReport.filterSpec.type === 'none') {
			return '';
		}

		if (selectedReport.filterSpec.type === 'date') {
			return formatDate(reportDates.startDate);
		}

		if (selectedReport.filterSpec.type === 'scope' && reportScope === 'all') {
			return '';
		}

		return `${formatDate(reportDates.startDate)} - ${formatDate(reportDates.endDate)}`;
	}, [reportDates, selectedReport, reportScope]);

	const reportTypeHelperText = useMemo(() => {
		if (!selectedReport) {
			return t(`${prefixFields}report_type.empty.helper_text`);
		}

		return t(`${prefixFields}report_type.${selectedReport.reportKey}.helper_text`);
	}, [prefixFields, selectedReport, t]);

	const applyDefaultDatesByFilterSpec = (filterType?: string, reportKey?: IReportCatalogItem['reportKey']) => {
		if (filterType === 'date') {
			const d = reportKey === 'balances_report' ? getBalancesMaxDate() : defineds.startOfToday;
			dispatch(setReportDates({ startDate: d, endDate: d }));
			return;
		}

		if (filterType === 'dateRange' || filterType === 'beneficiaryDateRange') {
			dispatch(
				setReportDates({
					startDate: defineds.startOfMonth,
					endDate: new Date(),
				})
			);
			return;
		}

		if (filterType === 'scope') {
			dispatch(
				setReportDates({
					startDate: defineds.startOfMonth,
					endDate: new Date(),
				})
			);
			return;
		}

		dispatch(
			setReportDates({
				startDate: defineds.startOfToday,
				endDate: defineds.startOfToday,
			})
		);
	};

	const handleReportSelection = (reportKey?: IReportCatalogItem['reportKey'], catalogOverride?: IReportCatalogItem[]) => {
		if (!reportKey) {
			dispatch(setSelectedReportKey(''));
			setSelectedBeneficiaryId('');
			return;
		}

		dispatch(setSelectedReportKey(reportKey));

		const reportsSource = catalogOverride || reportCatalog;
		const report = reportsSource.find((item) => item.reportKey === reportKey);
		if (report) {
			setSelectedFormat(report.defaultFormat || report.supportedFormats[0] || 'pdf');
			setSelectedBeneficiaryId('');
			if (report.filterSpec.type === 'scope') {
				setReportScope('all');
			}
			applyDefaultDatesByFilterSpec(report.filterSpec.type, report.reportKey);
		}
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		if (name === ReportInputFields.REPORT_TYPE) {
			handleReportSelection(value as IReportCatalogItem['reportKey']);
		}
		if (name === 'reportFormat') {
			setSelectedFormat(value as ReportFormat);
		}
		if (name === 'reportScope') {
			setReportScope(value as 'all' | 'byCreationDate');
		}
		if (name === 'reportBeneficiary') {
			setSelectedBeneficiaryId(value);
		}
		if (name === ReportInputFields.DATE_RANGE && isBalancesSingleDateReport(selectedReport)) {
			const parsed = value ? new Date(value) : null;
			if (parsed && isValidDate(parsed) && startOfDay(parsed).getTime() <= getBalancesMaxDate().getTime()) {
				dispatch(setReportDates({ startDate: parsed, endDate: parsed }));
			}
		}
	};

	const handleLocaleChange = (_event: React.MouseEvent<HTMLElement>, locale: ReportLocale | null) => {
		if (!locale) {
			return;
		}

		setReportLocale(locale);
	};

	const handleFormatChange = (_event: React.MouseEvent<HTMLElement>, format: ReportFormat | null) => {
		if (!format) {
			return;
		}

		setSelectedFormat(format);
	};

	const reportOptions = useMemo(() => {
		return reportCatalog.map((report) => {
			const translationKey = reportsCatalogTranslationKeys[report.reportKey];
			return {
				value: report.reportKey,
				label: translationKey ? t(translationKey, report.displayName) : report.displayName,
			};
		});
	}, [reportCatalog, t]);

	const reportFields = useMemo(() => {
		const baseFields: any[] = [
			{
				type: fieldType.select,
				props: {
					name: ReportInputFields.REPORT_TYPE,
					label: reportTypeLabel,
					placeholder: reportTypePlaceholder,
					value: selectedReportKey,
					options: reportOptions,
					helperText: reportTypeHelperText,
					disabled: loading,
				},
				renderOption: (props: React.HTMLAttributes<HTMLLIElement>, option: { label: string }) => (
					<li
						{...props}
						style={{
							...props.style,
							direction: lang === 'he' ? 'rtl' : 'ltr',
							textAlign: lang === 'he' ? 'right' : 'left',
						}}
					>
						{option.label}
					</li>
				),
			},
		];

		if (selectedReport && selectedReport.filterSpec.type === 'scope') {
			baseFields.push({
				type: fieldType.radio,
				props: {
					name: 'reportScope',
					value: reportScope,
					label: t(`${prefixFields}scope.label`),
					options: [
						{ value: 'all', label: t(`${prefixFields}scope.all.text`) },
						{ value: 'byCreationDate', label: t(`${prefixFields}scope.by_creation_date.text`) },
					],
					disabled: loading,
				},
			});
		}

		const showDateRange =
			selectedReport &&
			selectedReport.filterSpec.type !== 'none' &&
			(selectedReport.filterSpec.type === 'date' ||
				selectedReport.filterSpec.type === 'dateRange' ||
				selectedReport.filterSpec.type === 'beneficiaryDateRange' ||
				(selectedReport.filterSpec.type === 'scope' && reportScope === 'byCreationDate'));

		if (showDateRange) {
			if (isBalancesSingleDateReport(selectedReport)) {
				baseFields.push({
					type: fieldType.datePicker,
					props: {
						name: ReportInputFields.DATE_RANGE,
						label: dateLabel,
						placeholder: dateRangeLabel,
						value: reportDates.startDate ?? null,
						maxDate: getBalancesMaxDate(),
						disabled: loading,
					},
				});
			} else {
				baseFields.push({
					type: fieldType.dateRangePicker,
					props: {
						name: ReportInputFields.DATE_RANGE,
						label: selectedReport.filterSpec.type === 'date' ? dateLabel : dateRangeLabel,
						placeholder: dateRangeLabel,
						value: dateRangeValue,
						disabled: loading,
					},
				});
			}
		}

		if (selectedReport && selectedReport.filterSpec.type === 'beneficiaryDateRange') {
			baseFields.push({
				type: fieldType.select,
				props: {
					name: 'reportBeneficiary',
					label: t(`${prefixFields}beneficiary.label`),
					placeholder: t(`${prefixFields}beneficiary.placeholder`),
					value: selectedBeneficiaryId,
					options: beneficiaries.map((beneficiary: any) => ({
						value: String(beneficiary.id),
						label:
							beneficiary.nickname ||
							beneficiary.companyName ||
							`${beneficiary.firstName || ''} ${beneficiary.lastName || ''}`.trim() ||
							String(beneficiary.id),
					})),
					disabled: loading || loadBeneficiariesState.loadingBeneficiaries,
				},
			});
		}

		return baseFields;
	}, [
		dateLabel,
		dateRangeLabel,
		dateRangeValue,
		loading,
		loadBeneficiariesState.loadingBeneficiaries,
		lang,
		reportDates.startDate,
		beneficiaries,
		prefixFields,
		reportOptions,
		reportScope,
		reportTypeHelperText,
		reportTypeLabel,
		reportTypePlaceholder,
		selectedBeneficiaryId,
		selectedReport,
		selectedReportKey,
		t,
	]);

	const handleDownloadClick = async () => {
		if (isDownloadDisabled) {
			return;
		}

		const response: any = await dispatch(
			downloadReport({
				reportKey: selectedReport.reportKey,
				locale: reportLocale,
				format: selectedFormat,
				filters,
			})
		);

		if (!response) {
			return;
		}

		const fileName = buildReportFileName(
			selectedReport,
			selectedReport.filterSpec.type,
			filters as Record<string, any>,
			reportLocale,
			selectedFormat,
			t,
			selectedBeneficiaryDisplayName
		);

		downloadBlobResponse(response, fileName);
	};

	const [catalogLoaded, setCatalogLoaded] = useState(false);

	useEffect(() => {
		return () => {
			dispatch(resetReportsForm());
		};
	}, [dispatch]);

	useEffect(() => {
		if (selectedReport?.filterSpec?.type !== 'beneficiaryDateRange') {
			return;
		}

		if (beneficiaries.length > 0 || beneficiariesRequested) {
			return;
		}

		setBeneficiariesRequested(true);
		dispatch(loadBeneficiaries());
	}, [
		beneficiaries.length,
		beneficiariesRequested,
		dispatch,
		selectedReport?.filterSpec?.type,
	]);

	useEffect(() => {
		if (reportCatalog.length > 0) {
			setCatalogLoaded(true);
			return;
		}
		dispatch(fetchReportsCatalog()).then(() => {
			setCatalogLoaded(true);
		});
	}, [dispatch, reportCatalog.length]);

	const isEmpty = catalogLoaded && reportCatalog.length === 0;
	const isDownloadLoading = catalogLoaded && loading;
	const supportedFormats = selectedReport?.supportedFormats || ['pdf'];
	const isDownloadDisabled =
		loading ||
		!selectedReport ||
		(selectedReport.filterSpec.type === 'date' && !reportDates.startDate) ||
		(selectedReport.filterSpec.type === 'dateRange' && (!reportDates.startDate || !reportDates.endDate)) ||
		(selectedReport.filterSpec.type === 'beneficiaryDateRange' &&
			(!selectedBeneficiaryId || !reportDates.startDate || !reportDates.endDate)) ||
		(selectedReport.filterSpec.type === 'scope' &&
			reportScope === 'byCreationDate' &&
			(!reportDates.startDate || !reportDates.endDate));

	return (
		<Fragment>
			<ImagePopup
				open={userMessage.exist}
				image={WARNING_EXCLAMATION_ICON}
				title={userMessage.text ? t(userMessage.text, userMessage.text) : t('errors.code.500')}
				buttons={[
					{
						label: t('servers.errors.general.close_button.text'),
						variant: 'outlined',
						onClick: () => {
							dispatch(setMessage({ ...userMessage, exist: false }));
						},
						autoFocus: true,
					},
				]}
			/>
			<Page>
				<Stack spacing={1} sx={{ position: 'relative', height: '100%' }}>
					<CenteredNarrowContent>
						<FormCard>
							<FormHeader1 title={title} />
							{isEmpty ? (
								<Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
									{t(`${prefixFields}empty_state.text`, 'No reports available')}
								</Typography>
							) : (
								<>
									<Typography variant="body3" sx={{ marginTop: '0!important' }}>
										{subtitle}
									</Typography>
									<FormCreator
										name="report"
										handleChange={handleChange}
										fieldsGroups={[
											{
												type: fieldsGroupType.col,
												fields: reportFields,
											},
										]}
									/>
									<Stack spacing={2} sx={{ marginTop: '16px!important' }}>
										<Stack spacing={1} alignItems="flex-start">
											<Typography variant="body2">{t(`${prefixFields}language.label`)}</Typography>
											<ToggleButtonGroup
												exclusive
												value={reportLocale}
												onChange={handleLocaleChange}
												disabled={loading}
												size="small"
											>
												<ToggleButton value="he">{t(`${prefixFields}language.he.text`)}</ToggleButton>
												<ToggleButton value="en">{t(`${prefixFields}language.en.text`)}</ToggleButton>
											</ToggleButtonGroup>
										</Stack>
										{selectedReport && (
											<Stack spacing={1} alignItems="flex-start">
												<Typography variant="body2">{t(`${prefixFields}format.label`)}</Typography>
												<ToggleButtonGroup
													exclusive
													value={selectedFormat}
													onChange={handleFormatChange}
													disabled={loading}
													size="small"
												>
													{supportedFormats.map((format) => (
														<ToggleButton key={format} value={format}>
															{format.toUpperCase()}
														</ToggleButton>
													))}
												</ToggleButtonGroup>
											</Stack>
										)}
									</Stack>
								</>
							)}
						</FormCard>
						{isEmpty ? (
							<></>
						) : (
							<FormButton sx={{ marginTop: '24px', display: 'flex', justifyContent: 'center', gap: 2 }}>
								<Button
									sx={{ width: '35%' }}
									variant="contained"
									onClick={handleDownloadClick}
									disabled={isDownloadDisabled}
								>
									{isDownloadLoading
										? t(`${prefixFields}download_button_loading.text`)
										: t(`${prefixFields}download_button.text`)}
								</Button>
							</FormButton>
						)}
					</CenteredNarrowContent>
				</Stack>
			</Page>
		</Fragment>
	);
}
